import React, { createContext, useContext, useState, useEffect } from 'react';
import authService, { User } from '../services/auth';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  canView: () => boolean;
  canEdit: () => boolean;
  canRelease: () => boolean;
  canAdmin: () => boolean;
  isAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Error fetching user:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshUser();
  }, []);

  const login = async () => {
    const authUrl = await authService.login();
    window.location.href = authUrl;
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    window.location.href = '/login';
  };

  const canView = () => {
    return user?.role === 'admin' || user?.role === 'editor' || user?.role === 'releaser' || user?.role === 'viewer';
  };

  const canEdit = () => {
    return user?.role === 'admin' || user?.role === 'editor';
  };

  const canRelease = () => {
    return user?.role === 'admin' || user?.role === 'editor' || user?.role === 'releaser';
  };

  const canAdmin = () => {
    return user?.role === 'admin';
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, canView, canEdit, canRelease, canAdmin, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};